import axios from "axios";
import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/Layout";
import { ExportToExcel, ExportToPDF } from "../../Utilities/CommonMethods";

function CompanyListing() {
  const { state } = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [pages, setPages] = useState(1);
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Debounce function
  const debounce = (func, delay) => {
    let debounceTimer;
    return function() {
      const context = this;
      const args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(context, args), delay);
    };
  };

  // Implement the debounced search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debouncedSearch(e.target.value);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      setDebouncedSearchTerm(value);
      setCurrentPage(1); // Reset to first page when search term changes
    }, 500), // 500ms delay
    []
  );

  const getData = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const obj = {
      page: currentPage,
      limit: 8,
      search: debouncedSearchTerm
    };
    
    axios
      .post(`${BASE_URL}admin/getAllCompanies`, obj, {
        headers: {
          "x-access-token": `${token}`,
          "Content-Type": "application/json",
          version: "1.0.0",
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setAllCompanies(response.data.data);
          setPages(response.data.remainingCount);
        } else {
          if (response.data.error_code === 461) {
            navigate("/login");
          }
          setAllCompanies([]);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err.message);
        setAllCompanies([]);
      });
  }, [BASE_URL, currentPage, debouncedSearchTerm, navigate]);

  const DeleteCompany = (id) => {
    const token = localStorage.getItem('token');
    if (!window.confirm("Do you really want to delete this item?")) {
      return;
    }
    axios
      .delete(`${BASE_URL}admin/deleteCompany/${id}`, {
        headers: {
          'x-access-token': `${token}`,
          "Content-Type": "application/json",
          version: "1.0.0",
        },
      })
      .then((response) => {
        if (response.data.success) {
          toast.error(response.data.message);
          getData();
        }
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
  };

  useEffect(() => {
    getData();
  }, [getData, currentPage, debouncedSearchTerm]); // Trigger when debounced search term changes

  return (
    <Layout>
      <div className="content-i">
        <div className="content-box">
          <div className="element-wrapper">
            <div className="element-box">
              <h5 className="form-header">Company Listing</h5>
              <div className="row d-flex justify-content-between my-4">
                <div className="col">
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="Search by name, mobile, or type"
                      className="form-control searchLoan"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                    {searchTerm && (
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary" 
                        onClick={clearSearch}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-end">
                <button className="btn btn-primary" onClick={() => ExportToPDF(allCompanies, "Company")}>Export To PDF</button>{" "}
                <button className="btn btn-primary" onClick={() => ExportToExcel(allCompanies, ["_id", "created_on", "created_by_id", "isDeleted", "createdAt", "__v"])}>Export To Excel</button>
              </div>
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <table className="table table-lightborder">
                    <thead>
                      <tr>
                        <th>Company Type</th>
                        <th>Company Name</th>
                        <th>Company Coordinator Name</th>
                        <th>Company Contact Person Name</th>
                        <th>Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allCompanies?.length > 0 ? (
                        allCompanies.map((item, index) => (
                          <tr key={index}>
                            <td>{item.company_type}</td>
                            <td>{item.company_name}</td>
                            <td>
                              <div>
                                <p className="m-1">{item.company_coordinator_name}</p>
                                <p>{item.company_coordinator_mobile_no}</p>
                              </div>
                            </td>
                            <td>
                              <div>
                                <p className="m-1">{item.company_contact_person_name}</p>
                                <p>{item.company_contact_person_mobile_no}</p>
                              </div>
                            </td>
                            <td>{item.status}</td>
                            <td>
                              <button
                                className="border-0 bg-white"
                                onClick={() =>
                                  navigate("/add-company", { state: { item } })
                                }
                              >
                                <i className="os-icon os-icon-pencil-2" />
                              </button>
                              <button
                                className="text-danger border-0 bg-white ps-2"
                                onClick={() => DeleteCompany(item._id)}
                              >
                                <i className="os-icon os-icon-database-remove" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            {debouncedSearchTerm 
                              ? `No companies found matching "${debouncedSearchTerm}"` 
                              : "No companies found"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            {pages > 1 && (
              <nav aria-label="Page navigation">
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      aria-label="Previous"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <span aria-hidden="true">&laquo;</span>
                    </button>
                  </li>
                  {Array.from({ length: pages }, (_, i) => i + 1).map(
                    (page) => (
                      <li
                        key={page}
                        className={`page-item ${page === currentPage ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    )
                  )}
                  <li className={`page-item ${currentPage === pages ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      aria-label="Next"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === pages}
                    >
                      <span aria-hidden="true">&raquo;</span>
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default CompanyListing;